import { useQuery } from '@tanstack/react-query';
import { brandClient } from '@/lib/brand-client';
import { masterLineOf } from '@/lib/session-scope';
import type { ScopedBrand } from '@/types/auth';
import type { BrandStatusResponse } from '@/types/brand';

/**
 * The line status (phone, agent enabled/paused) of one brand — the kill switch's source
 * of truth once the aggregate filter is narrowed to a single brand.
 *
 * The session catalogue already carries the same values as of the last tree resolution,
 * so it seeds `placeholderData`: the switch paints immediately and then settles on the
 * live answer instead of flashing in.
 */
export function useBrandStatus(brand: ScopedBrand | null) {
  return useQuery<BrandStatusResponse>({
    queryKey: ['brand-status', brand?.id],
    queryFn: () => brandClient.getStatus(brand?.id),
    enabled: Boolean(brand),
    placeholderData: brand ? statusFromCatalogue(brand) : undefined,
  });
}

function statusFromCatalogue(brand: ScopedBrand): BrandStatusResponse {
  const line = masterLineOf(brand);
  return {
    phone: line?.phone ?? '',
    aiAgentEnabled: line?.aiAgentEnabled ?? false,
    aiAgentPaused: line?.aiAgentPaused ?? false,
    hasBrand: true,
  };
}
